import React, { FC, KeyboardEvent, useState } from 'react';
import { pick } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Form as FormikForm, Formik } from 'formik';
import { Box, Divider, Paper, Typography, Unstable_Grid2 as Grid } from '@mui/material';

import { useNotify, useProtectedClient } from 'hooks';
import {
  ContentEmailSpecFragment,
  useGetContentEmailSpecQuery,
  useModifyContentEmailMutation,
  UserSegmentFragment,
} from 'api';

import { ConditionalNumberTooltip, Loaders } from 'components';
import { EMPTY_SLATE_EDITOR } from 'app-constants';
import * as paths from 'paths';
import { formatStat } from 'helpers';

import ContentEmailsForm from 'features/admin/components/ContentEmails/Partials/Form';
import { ContentEmailSchema } from 'features/admin/validations';
import { useModifyContentEmail } from 'features/admin/updaters';

interface Props {
  segments: UserSegmentFragment[];
  userCount: number;
}

const ContentEmailsEdit: FC<Props> = ({ segments, userCount }) => {
  const { email_id } = useParams<{ email_id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notify = useNotify();
  const client = useProtectedClient();

  const [recipientCount, setRecipientCount] = useState(userCount);

  const { data, isLoading } = useGetContentEmailSpecQuery(
    { id: email_id },
    {
      onError: notify.queryError,
    }
  );

  const mutationOptions = useModifyContentEmail();
  const { mutate: modifyEmailMutation } = useModifyContentEmailMutation({
    onSuccess: handleCompleted,
    ...mutationOptions,
  });

  function handleCompleted() {
    notify.info({ message: t('contentEmails:saveSuccess') });
    navigate(paths.adminContentEmails);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  function processValues() {
    const { getContentEmailSpec } = data;
    const { email_from_address } = client;

    const tempInput = pick(
      getContentEmailSpec,
      'id',
      'title',
      'segment_ids',
      'all_users',
      'content_specs',
      'config_status',
      'send_at',
      'delivery_interval',
      'grouping_interval',
      'intro',
      'banner',
      'banner_url',
      'footer',
      'email_from',
      'email_reply_to',
      'subject',
      'use_sto'
    );

    tempInput.title = tempInput.title || '';
    tempInput.intro = tempInput.intro || EMPTY_SLATE_EDITOR;
    tempInput.subject = tempInput.subject || '';
    tempInput.footer = tempInput.footer || EMPTY_SLATE_EDITOR;
    tempInput.email_from = tempInput.email_from || email_from_address;
    tempInput.email_reply_to = tempInput.email_reply_to || email_from_address;

    return tempInput;
  }

  function handleSubmit(values: ContentEmailSpecFragment) {
    modifyEmailMutation({ data: values });
  }

  if (isLoading) {
    return <Loaders.Standard />;
  }

  return (
    <Paper>
      <Box padding={3}>
        <Grid container spacing={4} alignItems="center" justifyContent="space-between">
          <Grid>
            <Typography variant="h4">{t('contentEmails:editEmail')}</Typography>
          </Grid>

          <Grid>
            <Typography display="inline" variant="subtitle2">{`${t(
              'messages:recipients'
            )}: `}</Typography>
            <ConditionalNumberTooltip value={recipientCount}>
              <Typography display="inline" variant="subtitle2" color="primary">
                {formatStat(recipientCount)}
              </Typography>
            </ConditionalNumberTooltip>
          </Grid>
        </Grid>
      </Box>
      <Divider />
      <Formik
        enableReinitialize
        initialValues={processValues()}
        validationSchema={ContentEmailSchema(client)}
        onSubmit={handleSubmit}
      >
        <FormikForm onKeyDown={handleKeyDown}>
          <Box padding={3}>
            <ContentEmailsForm
              segments={segments}
              updateCount={setRecipientCount}
              totalUserCount={userCount}
            />
          </Box>
        </FormikForm>
      </Formik>
    </Paper>
  );
};

export default ContentEmailsEdit;
